import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { get, post, isAuthenticated } from '../utils/api';
import { message, Watermark, Form, Input, Button, List, Progress, Popover } from 'antd';
import QRCode from 'qrcode.react';
import Barcode from 'react-barcode';
import CatModification from './CatModification';
import GradientButton from '../components/GradientButton';
import VaccinationStatus from '../components/VaccinationStatus';
import './css/CatDetailPage.css';
import CatAvatar from '../components/img/avatars/CatAvatar.png';
import CatInfoHidden from '../components/img/icon_images/cat_info_hidden.png';

const { TextArea } = Input;

const CatDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cat, setCat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModificationModal, setShowModificationModal] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [comments, setComments] = useState([]);
  const [form] = Form.useForm();

  const isUserLoggedIn = isAuthenticated();

  useEffect(() => {
    setIsLoggedIn(isUserLoggedIn);
    fetchCatDetails();
    fetchComments();
  }, [id, isUserLoggedIn]);

  const fetchCatDetails = async () => {
    try {
      const data = await get(`/cat/${id}/`);
      console.log('Cat details response:', data);
      setCat(data);
      setIsPrivate(data.is_private);
      
      const userId = localStorage.getItem('userid');
      setIsOwner(userId === data.owner.toString());
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching cat details:', error);
      if (error.response && error.response.status === 403) {
        message.warning('此猫咪的信息已隐藏');
      } else {
        message.error('Failed to fetch cat details');
      }
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const data = await get(`/cat/${id}/comments/`);
      console.log('Fetched comments:', data);
      setComments(data);
    } catch (error) {
      console.error('Error fetching comments:', error);
      message.error('Failed to fetch comments');
    }
  };

  const handleSubmitComment = async (values) => {
    try {
      const commentData = {
        ...values,
        cat: id
      };
      console.log('Submitting comment:', commentData);
      const data = await post(`/cat/${id}/comments/`, commentData);
      console.log('Comment submission response:', data);
      message.success('Comment submitted successfully');
      form.resetFields();
      fetchComments();
    } catch (error) {
      console.error('Error submitting comment:', error);
      if (error.response) {
        message.error(`Failed to submit comment: ${error.response.data.detail || ' error'}`);
      } else {
        message.error('Failed to submit comment: Network error');
      }
    }
  };

  const openModificationModal = () => {
    setShowModificationModal(true);
  };

  const closeModificationModal = () => {
    setShowModificationModal(false);
    fetchCatDetails();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-CN');
  };

  const handleCollarClick = () => {
    // Open in new tab and pass cat ID
    window.open(`/collar-selection/${cat.id}`, '_blank');
  };

  const content = (
    <div>
      定制属于这只猫咪的专属二维码项圈，帮助猫咪找到家
    </div>
  );

  if (loading) {
    return <div className="cat-detail-loading">Loading...</div>;
  }

  if (!cat) {
    return (
        <div className="cat-detail-not-found">
           <img src={CatInfoHidden} alt="Cat info hidden" className="cat-detail-hidden-image" />
          <h1>未找到此猫咪信息或已隐藏</h1>
        </div>
    );
  }

  const catInfoUrl = `https://mao-mi.com/cat/${cat.unique_id}`;
  const watermarkContent = ['mao-mi.com', cat.unique_id];

  return (
    <div className="cat-detail-container">
      {isLoggedIn && isOwner && (
        <div className="cat-detail-modify-button-container">
          <GradientButton onClick={openModificationModal} className="cat-detail-modify-button">
            修改信息
          </GradientButton>
        </div>
      )}
      <div className="cat-detail-page">
          <div className="cat-detail-card">
            <div className="cat-detail-row">
              <div className="cat-detail-col-4">
                <div className="cat-detail-avatar-container">
                  <div className='avatar-div'>
                    <img src={cat.photos && cat.photos.length > 0 ? cat.photos[0] : CatAvatar} className="cat-detail-avatar" alt={cat.name} />
                  </div>
                  <VaccinationStatus 
                    vaccinations={{
                      first: cat.first_vaccination_date,
                      second: cat.second_vaccination_date,
                      third: cat.third_vaccination_date,
                      rabies: cat.rabies_vaccination_date,
                    }}
                    size={45} 
                  />
                </div>
                <div><Barcode className="cat-detail-barcode" value={cat.unique_id} width={0.9} height={15} displayValue={false} /></div>
              </div>
              <div className="cat-detail-col-8">
                <div className="cat-detail-row">
                  <div className="cat-detail-col-6">
                    <div className="cat-detail-title">{cat.name}</div>
                    <div className='collar-button-wrapper'>
                      <Popover content={content} placement="top">
                        <Button 
                          type="primary"
                          onClick={handleCollarClick}
                          className="collar-button"
                        >
                          防丢项圈
                        </Button>
                      </Popover>
                    </div>
                  </div>
                  <div className="cat-detail-col-6 qr-collar-section">
                    <QRCode value={catInfoUrl} size={80} />
                  </div>
                </div>
                <div className="cat-detail-description">
                  <div className="cat-detail-info-column">
                    <div><strong>品种:</strong> {cat.breed ? `${cat.breed.name_cn} (${cat.breed.name_en})` : ''}</div>
                    <div><strong>出生日期:</strong> {formatDate(cat.birth_date)}</div>
                    <div><strong>出生城市:</strong> {cat.birth_city_name || ''}</div>
                    <div><strong>性别:</strong> {cat.sex === 'M' ? '公猫' : '母猫'}</div>
                    <div><strong>体重:</strong> {cat.weight ? `${cat.weight} kg` : ''}</div>
                    <div><strong>唯一ID:</strong> {cat.unique_id}</div>
                    {isOwner && <div><strong>隐私状态:</strong> {isPrivate ? '私密' : '公开'}</div>}
                  </div>
                  <div className="cat-detail-attributes-column">
                    <div>
                      <strong>粘人值:</strong>
                      <Progress percent={cat.clinginess * 20} showInfo={false} />
                      <span>{cat.clinginess || 0}/5</span>
                    </div>
                    <div>
                      <strong>可爱值:</strong>
                      <Progress percent={cat.cuteness * 20} showInfo={false} />
                      <span>{cat.cuteness || 0}/5</span>
                    </div>
                    <div>
                      <strong>健康状况:</strong>
                      <Progress percent={cat.health * 20} showInfo={false} />
                      <span>{cat.health || 0}/5</span>
                    </div>
                    <div>
                      <strong>贪吃值:</strong>
                      <Progress percent={cat.gluttony * 20} showInfo={false} />
                      <span>{cat.gluttony || 0}/5</span>
                    </div>
                    <div>
                      <strong>贪睡值:</strong>
                      <Progress percent={cat.sleepiness * 20} showInfo={false} />
                      <span>{cat.sleepiness || 0}/5</span>
                    </div>
                    <div>
                      <strong>淘气值:</strong>
                      <Progress percent={cat.pettiness * 20} showInfo={false} />
                      <span>{cat.pettiness || 0}/5</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
      </div>
      {isLoggedIn && isOwner && cat && (
        <CatModification
          visible={showModificationModal}
          onClose={closeModificationModal}
          cat={cat}
          onUpdate={fetchCatDetails}
        />
      )}
      <div className="cat-detail-comment-section">
        <List
          itemLayout="horizontal"
          dataSource={comments}
          renderItem={item => (
            <div className="cat-detail-comment">
              <div className="cat-detail-comment-header">
                <small>{item.user ? item.user.username : '匿名用户'} 发表于 {new Date(item.comment_at).toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</small>
              </div>
              <p>{item.comment}</p>
            </div>
          )}
        />
        {isLoggedIn && (
          <Form form={form} onFinish={handleSubmitComment} className="cat-detail-comment-form">
            <div className="cat-detail-review-header">
              <span className="cat-detail-reply-label">回复：</span>
              <Button type="primary" htmlType="submit" className="cat-detail-submit-review">
                提交
              </Button>
            </div>
            <Form.Item name="comment" rules={[{ required: true, message: 'Please input your comment!' }]}>
              <TextArea rows={4} />
            </Form.Item>
          </Form>
        )}
      </div>
    </div>
  );
};

export default CatDetailPage;
